<template>
  <b-modal id="payment-gateway-config-modal" :title="`ตั้งค่า Payment Gateway${paymentGatewayInfo ? `: ${paymentGatewayInfo.name}` : ''}`" hide-footer>
    <div v-if="!paymentGateway || isFetching" class="text-center">
      <b-spinner />
    </div>
    <div v-else>
      <ValidationObserver v-slot="{ handleSubmit }">
        <b-form @submit.prevent="handleSubmit(onSubmit)">
          <div class="font-size-16 font-weight-bold mb-2">การใช้งาน</div>
          <b-row>
            <b-col cols="6">
              <b-form-group>
                <b-form-checkbox
                    id="is-deposit-enabled"
                    v-model="paymentGateway.isDepositEnabled"
                    :disabled="paymentGatewayInfo.isAllowedDeposit === false"
                    switch
                >
                  ฝาก
                </b-form-checkbox>
              </b-form-group>
            </b-col>
            <b-col cols="6">
              <b-form-group>
                <b-form-checkbox
                    id="is-withdraw-enabled"
                    v-model="paymentGateway.isWithdrawEnabled"
                    :disabled="paymentGatewayInfo.isAllowedWithdraw === false"
                    switch
                >
                  ถอน
                </b-form-checkbox>
              </b-form-group>
            </b-col>
            <b-col cols="6">
              <b-form-group>
                <b-form-checkbox
                    id="is-verify-account-enabled"
                    v-model="paymentGateway.isVerifyAccountEnabled"
                    :disabled="paymentGatewayInfo.isAllowedVerifyAccount === false"
                    switch
                >
                  ดึงชื่อ
                </b-form-checkbox>
              </b-form-group>
            </b-col>
            <b-col cols="6">
              <b-form-group>
                <b-form-checkbox
                    id="is-verify-slip-enabled"
                    v-model="paymentGateway.isVerifySlipEnabled"
                    :disabled="paymentGatewayInfo.isAllowedVerifySlip === false"
                    switch
                >
                  เช็คสลิป
                </b-form-checkbox>
              </b-form-group>
            </b-col>
          </b-row>

          <hr />
          <div class="font-size-16 font-weight-bold mb-2">การตั้งค่า</div>
          <ValidationProvider v-slot="{ errors }" name="ยอดฝากขั้นต่ำ" rules="min_value:1">
            <b-form-group description="เว้นว่างไว้ = ไม่กำหนดขั้นต่ำ" label="ยอดฝากขั้นต่ำ (บาท)">
              <b-form-input
                  v-model="paymentGateway.config.minDepositAmount"
                  :state="errors[0] ? false : null"
                  min="1"
                  name="minDepositAmount"
                  type="number"
              />
              <b-form-invalid-feedback>
                {{ errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </ValidationProvider>
          <ValidationProvider
              v-for="(config, i) in paymentGatewayInfo.configs"
              :key="i"
              v-slot="{ errors }"
              :name="config.key"
              rules="required"
          >
            <b-form-group :label="config.label">
              <b-select
                  v-if="config.options"
                  v-model="paymentGateway.config[config.key]"
                  :name="config.key"
                  :state="errors[0] ? false : null"
              >
                <b-form-select-option :value="null" disabled>เลือก{{ config.label }}</b-form-select-option>
                <b-form-select-option
                    v-for="(option, optionI) in config.options"
                    :key="optionI"
                    :value="typeof option === 'object' ? option.value : option"
                >{{ typeof option === 'object' ? option.text : option }}
                </b-form-select-option>
              </b-select>
              <b-form-input
                  v-else
                  v-model="paymentGateway.config[config.key]"
                  :aria-invalid="false"
                  :name="config.key"
                  :state="errors[0] ? false : null"
                  type="text"
              />
              <b-form-invalid-feedback>
                {{ errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </ValidationProvider>

          <div class="text-right">
            <b-button class="mr-2" type="button" variant="secondary" @click="$bvModal.hide('payment-gateway-config-modal')">
              ยกเลิก
            </b-button>
            <b-button :disabled="isSubmitting" type="submit" variant="primary">
              บันทึก
            </b-button>
          </div>
        </b-form>
      </ValidationObserver>
    </div>
  </b-modal>
</template>

<script>
import {mapActions} from 'vuex';
import {PAYMENT_GATEWAY} from '@src/constants/payment-gateway';

export default {
  name: 'PaymentGatewayConfigModal',
  props: {
    id: {
      type: String,
      required: false,
      default: null
    }
  },
  data() {
    return {
      isFetching: false,
      paymentGateway: null,
      isSubmitting: false,
    }
  },
  computed: {
    paymentGatewayInfo() {
      return this.paymentGateway?.name ? PAYMENT_GATEWAY[this.paymentGateway?.name] : null
    }
  },
  created() {
    this.$root.$on('bv::modal::show', (bvEvent, modalId) => {
      if (modalId === 'payment-gateway-config-modal') {
        this.fetchData()
      }
    })
  },
  methods: {
    ...mapActions(['getPaymentGatewayDetail', 'updatePaymentGateway', 'getPaymentGatewaySummaries']),
    async fetchData() {
      if (!this.id) return
      this.isFetching = true;
      this.paymentGateway = await this.getPaymentGatewayDetail(this.id)
      if (this.paymentGateway) {
        // minDepositAmount is optional, so declare it up front for Vue to track it
        this.$set(this.paymentGateway, 'config', this.paymentGateway.config ?? {})
        this.$set(this.paymentGateway.config, 'minDepositAmount', this.paymentGateway.config.minDepositAmount ?? '')
        // dropdown configs need a tracked value so the placeholder option is the one selected when unset
        this.paymentGatewayInfo?.configs
            ?.filter((config) => config.options)
            .forEach((config) => {
              this.$set(this.paymentGateway.config, config.key, this.paymentGateway.config[config.key] ?? null)
            })
      }
      this.isFetching = false;
    },
    async onSubmit() {
      this.isSubmitting = true
      const {minDepositAmount, ...config} = this.paymentGateway.config
      // the number input hands back a string, and an empty one means no minimum
      const minDeposit = Number(minDepositAmount)
      const isUpdated = await this.updatePaymentGateway({
        id: this.id,
        data: {
          ...this.paymentGateway,
          config: minDeposit > 0 ? {...config, minDepositAmount: minDeposit} : config
        }
      })
      if (isUpdated) {
        this.$bvModal.hide('payment-gateway-config-modal')
        this.$emit('onUpdate')
      }
      this.isSubmitting = false
    }
  }
}
</script>